import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient.js';

const UnassignedJobsPanel = ({ routeId, onAssignJob, visible, onClose }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetchJobs = useCallback(async () => {
    // Get booked crm_jobs not assigned to any route
    const { data: allBooked, error } = await supabase
      .from('crm_jobs')
      .select('*')
      .eq('booking_status', 'booked')
      .order('company', { ascending: true });

    if (error) {
      console.error('Error fetching unassigned jobs:', error);
      setLoading(false);
      return;
    }

    // Filter out jobs already assigned to a route
    const { data: assignedIds } = await supabase
      .from('route_items')
      .select('crm_job_id')
      .not('crm_job_id', 'is', null)
      .not('route_id', 'is', null);
    const assignedSet = new Set((assignedIds || []).map(r => r.crm_job_id));
    setJobs((allBooked || []).filter(j => !assignedSet.has(j.id)));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (visible) fetchJobs();
  }, [visible, fetchJobs]);

  // Realtime: refresh when jobs are added/removed
  useEffect(() => {
    if (!visible) return;

    const channel = supabase
      .channel('unassigned-jobs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_jobs' }, () => fetchJobs())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'route_items' }, () => fetchJobs())
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [visible, fetchJobs]);

  const handleAssign = async (job, dayNumber) => {
    // Check if already assigned (ignore orphaned route_items with null route_id)
    const { data: alreadyAssigned } = await supabase
      .from('route_items')
      .select('id')
      .eq('crm_job_id', job.id)
      .not('route_id', 'is', null)
      .limit(1);
    if (alreadyAssigned?.length > 0) {
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
      if (onAssignJob) onAssignJob();
      return;
    }

    const { error } = await supabase
      .from('route_items')
      .insert([{
        crm_job_id: job.id,
        route_id: routeId,
        row_type: 'JOB',
        day_number: dayNumber,
        sort_order: 999,
      }]);

    if (error) {
      console.error('Error assigning job:', error);
    } else {
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
      if (onAssignJob) onAssignJob();
    }
  };

  const filtered = filter
    ? jobs.filter(
        (j) =>
          (j.company || '').toLowerCase().includes(filter.toLowerCase()) ||
          (j.location || '').toLowerCase().includes(filter.toLowerCase()) ||
          (j.work_type || '').toLowerCase().includes(filter.toLowerCase())
      )
    : jobs;

  if (!visible) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-80 bg-gray-900 border-l border-gray-700 flex flex-col shadow-xl">
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold">Unassigned Jobs</h3>
          <p className="text-xs text-gray-400 mt-0.5">{jobs.length} job(s) available</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">&times;</button>
      </div>

      <div className="px-4 py-2 border-b border-gray-700">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by company, location..."
          className="w-full px-3 py-1.5 bg-gray-700 border border-gray-500 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-green-600"
        />
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">
            {filter ? 'No matches.' : 'No unassigned booked jobs.'}
          </p>
        ) : (
          filtered.map((job) => (
            <UnassignedJobCard key={job.id} job={job} onAssign={handleAssign} />
          ))
        )}
      </div>
    </div>
  );
};

const UnassignedJobCard = ({ job, onAssign }) => {
  const [showAssign, setShowAssign] = useState(false);
  const [day, setDay] = useState(1);

  const isBooked = job.booking_status === 'booked';

  return (
    <div
      className={`rounded-md px-3 py-2 text-sm border ${
        isBooked
          ? 'bg-gray-700 border-gray-500/50'
          : 'bg-gray-700/50 border-dashed border-gray-500/30 opacity-70'
      }`}
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-white truncate">{job.company || 'Unnamed'}</div>
          {job.location && <div className="text-gray-400 text-xs truncate">{job.location}</div>}
        </div>
        <div className="text-right shrink-0">
          {job.value && (
            <div className="text-green-400 text-xs font-medium">${parseFloat(job.value).toLocaleString()}</div>
          )}
          {job.estimated_hours && (
            <div className="text-gray-400 text-xs">{job.estimated_hours}h</div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <div className="flex gap-1">
          {job.booking_status && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              isBooked ? 'bg-green-600/20 text-green-400' :
              job.booking_status === 'tentative' ? 'bg-amber-500/20 text-amber-300' :
              'bg-gray-500/30 text-gray-400'
            }`}>
              {job.booking_status}
            </span>
          )}
          {job.work_type && (
            <span className="text-xs text-gray-400">{job.work_type}</span>
          )}
        </div>

        {!showAssign ? (
          <button
            onClick={() => setShowAssign(true)}
            className="text-xs text-green-400 hover:text-green-400/80 font-medium"
          >
            Assign
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400">Day:</span>
            <input
              type="number"
              min="1"
              value={day}
              onChange={(e) => setDay(parseInt(e.target.value) || 1)}
              className="w-12 px-1 py-0.5 bg-gray-900 border border-gray-500 rounded text-white text-xs text-center"
            />
            <button
              onClick={() => onAssign(job, day)}
              className="text-xs px-2 py-0.5 bg-green-700 text-white rounded hover:bg-green-600"
            >
              Go
            </button>
            <button
              onClick={() => setShowAssign(false)}
              className="text-xs text-gray-400 hover:text-white"
            >
              &times;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnassignedJobsPanel;
